import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/**
 * Service for notifying components when totalizers need to be refreshed
 *
 * This service implements the Observer pattern to allow components to react
 * to changes in totalizers data without tight coupling.
 *
 * Usage:
 *
 * 1. In components that display totalizers (e.g., TotalizerListComponent):
 *    - Inject the service in constructor
 *    - Subscribe to refreshTotalizers$ observable
 *    - Refresh data when event is emitted
 *
 *    Example:
 *    ```typescript
 *    constructor(private _totalizerRefreshService: TotalizerRefreshService) {}
 *
 *    ngOnInit() {
 *      this._totalizerRefreshService.refreshTotalizers$
 *        .pipe(takeUntil(this._unsubscribeAll))
 *        .subscribe(() => {
 *          this.loadTotalizersFromBackend();
 *        });
 *    }
 *    ```
 *
 * 2. In components that modify totalizers (e.g., ModalAddEvaluationComponent):
 *    - Inject the service in constructor
 *    - Call triggerRefresh() after successful operations
 *
 *    Example:
 *    ```typescript
 *    constructor(private _totalizerRefreshService: TotalizerRefreshService) {}
 *
 *    onSuccess() {
 *      this._totalizerRefreshService.triggerRefresh();
 *      this.activeModal.close(true);
 *    }
 *    ```
 */
@Injectable({
	providedIn: 'root',
})
export class TotalizerRefreshService {
	// Subject to emit refresh events
	private refreshTotalizersSubject = new Subject<void>();

	// Observable that components can subscribe to
	public refreshTotalizers$ = this.refreshTotalizersSubject.asObservable();

	// Subject to emit includeNotConfirmed state changes
	private includeNotConfirmedSubject = new BehaviorSubject<boolean>(false);

	// Observable for includeNotConfirmed state
	public includeNotConfirmed$ = this.includeNotConfirmedSubject.asObservable();

	/**
	 * Trigger a refresh of all totalizers
	 * Call this after successful registration/evaluation operations
	 * that may affect totalizers data (approve/reject, skill assignment, etc.)
	 */
	triggerRefresh(): void {
		this.refreshTotalizersSubject.next();
	}

	/**
	 * Set the includeNotConfirmed state and notify subscribers
	 * @param value - true to include unconfirmed registrations, false otherwise
	 */
	setIncludeNotConfirmed(value: boolean): void {
		this.includeNotConfirmedSubject.next(value);
	}

	/**
	 * Get the current includeNotConfirmed state value
	 */
	getIncludeNotConfirmedValue(): boolean {
		return this.includeNotConfirmedSubject.value;
	}
}
